/**
 * Password validation utilities
 *
 * Password requirements:
 * - Must be at least 8 characters long
 * - Must contain at least one special character (!@#$%^&*)
 * - Must contain at least one number
 */

export type PasswordValidationResult = {
  isValid: boolean;
  errors: string[];
};

const SPECIAL_CHARACTERS = /[!@#$%^&*]/;
const NUMBER = /[0-9]/;

/**
 * Validates a password against the requirements
 * @param password - The password to validate
 * @returns Validation result with isValid flag and array of error messages
 */
export const validatePassword = (password: string): PasswordValidationResult => {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push("password-validation-min-length");
  }

  if (!SPECIAL_CHARACTERS.test(password)) {
    errors.push("password-validation-special-character");
  }

  if (!NUMBER.test(password)) {
    errors.push("password-validation-number");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Gets a human-readable error message for a validation error key
 * @param errorKey - The error key from validation
 * @param translate - Translation function
 * @returns Translated error message
 */
export const getPasswordValidationErrorMessage = (errorKey: string, translate: (key: string) => string): string => {
  return translate(`general.${errorKey}`);
};
